import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { relativeTime } from '@/lib/format'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'

type Notification = Schema<'NotificationOut'>

/**
 * The notification centre — S-D3 screen 02, converted.
 *
 * **Why this shape** (the design's own words): notifications are the product's
 * voice when the tenant is not looking, so each one names what happened *and*
 * what it wants. Anything actionable carries its action inline, which is
 * `action_path` and `action_label` on the row — a path, never a URL, because the
 * same row is rendered into an email where the origin comes from the server.
 *
 * **Opening the panel does not mark anything read.** S-D3 draws a *Mark all
 * read* button, and having one implies the alternative is deliberate: a badge
 * that cleared itself the moment you glanced at it is a badge that loses the one
 * thing you had not dealt with. Clicking a row marks that row, because clicking
 * it is an answer.
 *
 * **The badge is polled, not pushed.** No WebSocket and no SSE: a server-sent
 * stream would mean a long-lived connection per open tab against a 0.25 vCPU
 * task (COGS §8), for a count that changes a handful of times a day. Sixty
 * seconds is the same interval the dispatcher runs on, so the badge cannot be
 * more than one tick behind what has actually been sent. Polling stops while the
 * tab is hidden — `document.hidden` — because a backgrounded tab counting
 * nothing is the cheap half of this to get wrong.
 */

/** How often the unread count is refreshed while the tab is visible. */
export const POLL_INTERVAL_MS = 60_000

/** How many rows the panel shows. Enough to be a history, short enough to scan. */
const PAGE = 20

const TONE: Record<string, string> = {
  info: 'ar-badge--info',
  success: 'ar-badge--success',
  warning: 'ar-badge--warning',
  danger: 'ar-badge--danger',
}

export function NotificationBell(): JSX.Element {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [tick, setTick] = useState(0)
  const wrapper = useRef<HTMLDivElement>(null)
  const now = new Date()

  const list = useQuery(
    (signal) =>
      call(() => api.GET('/api/v1/notifications', { params: { query: { limit: PAGE } }, signal })),
    [tick],
  )

  const markRead = useMutation((id: number) =>
    call(() =>
      api.POST('/api/v1/notifications/{notification_id}/read', {
        params: { path: { notification_id: id } },
      }),
    ),
  )
  const markAll = useMutation(() => call(() => api.POST('/api/v1/notifications/read-all', {})))

  const refresh = useCallback(() => {
    setTick((current) => current + 1)
  }, [])

  // The poll. `window.setInterval` rather than a chain of timeouts so a slow
  // response cannot make the interval drift; the visibility check is what stops
  // a backgrounded tab from counting nothing every minute for a week.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (!document.hidden) refresh()
    }, POLL_INTERVAL_MS)
    return () => {
      window.clearInterval(id)
    }
  }, [refresh])

  // Escape and click-away, exactly as the account menu does — people close a
  // panel by clicking the thing they wanted, not by clicking the button again.
  useEffect(() => {
    if (!open) return
    function onDocument(event: MouseEvent | KeyboardEvent): void {
      if (event instanceof KeyboardEvent) {
        if (event.key === 'Escape') setOpen(false)
        return
      }
      if (event.target instanceof Node && !wrapper.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocument)
    document.addEventListener('keydown', onDocument)
    return () => {
      document.removeEventListener('mousedown', onDocument)
      document.removeEventListener('keydown', onDocument)
    }
  }, [open])

  const unread = list.state.data?.unread ?? 0
  const rows = list.state.data?.notifications ?? []

  function act(row: Notification): void {
    void (async () => {
      if (!row.read) {
        const result = await markRead.run(row.id)
        if (result.ok) refresh()
      }
      if (row.action_path) {
        setOpen(false)
        void navigate(row.action_path)
      }
    })()
  }

  return (
    <div className="app-notif" ref={wrapper}>
      <button
        type="button"
        className={`ar-btn ar-btn--${open ? 'primary' : 'quiet'} ar-btn--sm ar-btn--icon`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={unread === 0 ? 'Notifications' : `Notifications, ${String(unread)} unread`}
        onClick={() => {
          setOpen((current) => {
            if (!current) refresh()
            return !current
          })
        }}
      >
        <span aria-hidden="true">🔔</span>
        {unread > 0 ? (
          <span className="app-notif__badge" aria-hidden="true">
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="app-notif__panel" role="dialog" aria-label="Notifications">
          <div className="app-notif__head">
            <strong>Notifications</strong>
            {unread > 0 ? <span className="ar-badge ar-badge--warning">{unread} new</span> : null}
            <span className="ar-card__spacer" />
            {unread > 0 ? (
              <Button
                tone="quiet"
                size="sm"
                busy={markAll.pending}
                busyLabel="Marking read"
                onClick={() => {
                  void (async () => {
                    const result = await markAll.run(undefined)
                    if (result.ok) refresh()
                  })()
                }}
              >
                Mark all read
              </Button>
            ) : null}
          </div>

          <InlineError failure={markRead.state.failure ?? markAll.state.failure} />

          {list.state.status === 'loading' ? (
            <div className="app-notif__body">
              <Loading label="Loading notifications" lines={3} />
            </div>
          ) : null}

          {list.state.status === 'error' ? (
            <ErrorState
              failure={list.state.failure}
              title="Could not load notifications"
              onRetry={refresh}
            />
          ) : null}

          {list.state.status === 'ready' && rows.length === 0 ? (
            <div className="app-notif__body">
              {/* S-D3: never an empty box. */}
              <Empty
                icon="◷"
                title="You're all caught up"
                body="We'll tell you when something needs you."
              />
            </div>
          ) : null}

          {list.state.status === 'ready' && rows.length > 0 ? (
            <ul className="app-notif__list">
              {rows.map((row) => (
                <li
                  key={row.id}
                  className="app-notif__item"
                  data-unread={row.read ? undefined : ''}
                >
                  <span
                    className={`app-notif__dot ${TONE[row.severity] ?? ''}`}
                    aria-hidden="true"
                  />
                  <div className="app-notif__content">
                    <strong>{row.title}</strong>
                    {row.body ? <span className="ar-muted">{row.body}</span> : null}
                    {row.action_path ? (
                      <div className="app-notif__action">
                        <Button
                          tone={row.severity === 'danger' ? 'danger' : 'primary'}
                          size="sm"
                          onClick={() => {
                            act(row)
                          }}
                        >
                          {row.action_label ?? 'Open'}
                        </Button>
                      </div>
                    ) : null}
                    <time dateTime={row.created_at}>{relativeTime(row.created_at, now)}</time>
                  </div>
                  {row.read ? null : (
                    <button
                      type="button"
                      className="app-notif__read"
                      aria-label={`Mark "${row.title}" read`}
                      onClick={() => {
                        void (async () => {
                          const result = await markRead.run(row.id)
                          if (result.ok) refresh()
                        })()
                      }}
                    >
                      ✓
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : null}

          <div className="app-notif__foot">
            <Link
              to="/settings/notifications"
              onClick={() => {
                setOpen(false)
              }}
            >
              Notification settings
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  )
}
